import { formatAudCompact } from "../Admissions/AddAdmissions/applicationFlow/termPaymentUtils";

export const parseNoteContent = (noteText) => {
  const attachmentMatch = noteText?.match(/\[ATTACHMENTS\](.*?)\[\/ATTACHMENTS\]/);
  if (attachmentMatch) {
    const attachments = attachmentMatch[1].split("|").filter(Boolean);
    const text = noteText.replace(/\[ATTACHMENTS\].*?\[\/ATTACHMENTS\]/, "").trim();
    return { text, attachments };
  }
  return { text: noteText || "", attachments: [] };
};

export const buildPaymentNoteText = ({ amount, date, reference, notes, attachments }) => {
  const parts = [`Payment ${formatAudCompact(Number(amount))} on ${date}`];
  if (reference?.trim()) parts.push(`Ref: ${reference.trim()}`);
  if (notes?.trim()) parts.push(notes.trim());
  const attachmentUrls =
    attachments?.length > 0 ? `[ATTACHMENTS]${attachments.join("|")}[/ATTACHMENTS]` : "";
  return `${parts.join(". ")}${attachmentUrls}`;
};

/** Stable prefix used to match a payment row to its linked Action-column note. */
export const paymentNotePrefix = (payment) => {
  const parts = [`Payment ${formatAudCompact(Number(payment.amount))} on ${payment.date}`];
  if (payment.reference?.trim()) parts.push(`Ref: ${payment.reference.trim()}`);
  return `${parts.join(". ")}`;
};

export const isSameNote = (a, b) => {
  if (!a || !b) return false;
  if (a.id != null && b.id != null && a.id === b.id) return true;
  if (a.uuid != null && b.uuid != null && a.uuid === b.uuid) return true;
  return false;
};

export const findPaymentForNote = (note, payments = []) =>
  payments.find((p) => isSameNote({ uuid: p.noteUuid }, note) || matchesPaymentNote(note, p));

const matchesPaymentNote = (note, payment) => {
  const prefix = paymentNotePrefix(payment);
  const { text } = parseNoteContent(note?.text || "");
  return text === prefix || text.startsWith(`${prefix}. `);
};

export const isPaymentNote = (note) => {
  const { text } = parseNoteContent(note?.text || "");
  return text.startsWith("Payment ");
};

export const getPaymentAmountLabelFromNote = (note) => {
  const { text } = parseNoteContent(note?.text || "");
  const match = text.match(/^Payment (\$[\d,]+(?:\.\d+)?)/);
  return match?.[1] || null;
};

export const getPaymentDeleteConfirmMessage = (noteOrPayment) => {
  const amountLabel =
    noteOrPayment?.amount != null
      ? formatAudCompact(Number(noteOrPayment.amount))
      : getPaymentAmountLabelFromNote(noteOrPayment);

  if (amountLabel) {
    return `This will remove the payment of ${amountLabel} from the Payments total and delete its linked note. This cannot be undone.`;
  }

  return "This will remove the linked payment from the Payments total and delete this note. This cannot be undone.";
};

export const getNoteDeleteConfirmMessage = (note) => {
  if (isPaymentNote(note)) {
    return getPaymentDeleteConfirmMessage(note);
  }
  return "Are you sure you want to delete this note? This cannot be undone.";
};

export const findNoteForPayment = (payment, notes = []) => {
  if (payment?.noteUuid) {
    const byUuid = notes.find((n) => n.uuid === payment.noteUuid);
    if (byUuid) return byUuid;
  }
  const prefix = paymentNotePrefix(payment);
  return notes.find((n) => {
    const { text } = parseNoteContent(n.text);
    return text === prefix || text.startsWith(`${prefix}. `);
  });
};

export const enrichPaymentFromNote = (payment, notes = []) => {
  const note = findNoteForPayment(payment, notes);
  if (!note) return payment;
  const { attachments } = parseNoteContent(note.text);
  return {
    ...payment,
    noteUuid: note.uuid,
    noteId: note.id,
    attachments: attachments.length ? attachments : payment.attachments || [],
  };
};

export const enrichPaymentsFromNotes = (payments, notes = []) =>
  (payments || []).map((p) => enrichPaymentFromNote(p, notes));

export const getPaymentNoteUserText = (payment, notes = []) => {
  if (payment.noteText) return payment.noteText;
  const note = findNoteForPayment(payment, notes);
  if (!note) return null;
  const { text } = parseNoteContent(note.text);
  const prefix = paymentNotePrefix(payment);
  if (text === prefix) return null;
  if (text.startsWith(`${prefix}. `)) {
    return text.slice(prefix.length + 2).trim() || null;
  }
  return null;
};

export const toDisplayPayment = (payment, notes = []) => {
  const enriched = enrichPaymentFromNote(payment, notes);
  return {
    ...enriched,
    noteText: getPaymentNoteUserText(enriched, notes),
  };
};

/** e.g. "RPL account verification", "Visa account verification" */
export const getModulePaymentTaskLabel = (moduleName) => {
  const name = (moduleName || "").trim();
  if (!name) return "Account payment verification";
  return `${name} account verification`;
};

/** Task title shown in Tasks list and pre-filled in assign modal */
export const buildModulePaymentSpecificTask = (moduleName, amount) => {
  const label = getModulePaymentTaskLabel(moduleName);
  const amt = Number(amount);
  if (!amt || amt <= 0) return label;
  return `${label} — ${formatAudCompact(amt)}`;
};
